import React, { FC, useState } from 'react';
import { MemoryItem, MemoryFact, MemoryRelationship } from '@dreamai/types';
import VoiceRecorder from './VoiceRecorder';

// Approve icon (green checkmark bookmark)
const ApproveIcon = () => (
  <svg
    xmlns='http://www.w3.org/2000/svg'
    width='20'
    height='20'
    viewBox='0 0 20 20'
    fill='none'
  >
    <path
      d='M7.5 8.75L9.16666 10.4167L12.9167 6.66667M15.8333 17.5V6.5C15.8333 5.09987 15.8333 4.3998 15.5608 3.86502C15.3212 3.39462 14.9387 3.01217 14.4683 2.77248C13.9335 2.5 13.2335 2.5 11.8333 2.5H8.16666C6.76653 2.5 6.06647 2.5 5.53169 2.77248C5.06128 3.01217 4.67883 3.39462 4.43915 3.86502C4.16666 4.3998 4.16666 5.09987 4.16666 6.5V17.5L10 14.1667L15.8333 17.5Z'
      stroke='#149685'
      strokeWidth='2'
      strokeLinecap='round'
      strokeLinejoin='round'
    />
  </svg>
);

// Decline icon (red X bookmark)
const DeclineIcon = () => (
  <svg
    xmlns='http://www.w3.org/2000/svg'
    width='20'
    height='20'
    viewBox='0 0 20 20'
    fill='none'
  >
    <path
      d='M7.91666 6.25L12.0833 10.4167M12.0833 6.25L7.91666 10.4167M15.8333 17.5V6.5C15.8333 5.09987 15.8333 4.3998 15.5608 3.86502C15.3212 3.39462 14.9387 3.01217 14.4683 2.77248C13.9335 2.5 13.2335 2.5 11.8333 2.5H8.16666C6.76653 2.5 6.06646 2.5 5.53168 2.77248C5.06128 3.01217 4.67882 3.39462 4.43914 3.86502C4.16666 4.3998 4.16666 5.09987 4.16666 6.5V17.5L9.99999 14.1667L15.8333 17.5Z'
      stroke='#EE4266'
      strokeWidth='2'
      strokeLinecap='round'
      strokeLinejoin='round'
    />
  </svg>
);

interface MemoryCardProps {
  item: MemoryItem;
  onApprove: (item: MemoryItem) => void;
  onDecline: (item: MemoryItem, correction: string) => void;
}

type CardMode = 'view' | 'correcting';

const MemoryCard: FC<MemoryCardProps> = ({ item, onApprove, onDecline }) => {
  const [mode, setMode] = useState<CardMode>('view');
  const [correctionText, setCorrectionText] = useState('');

  // Get context from raw data if it's a fact
  const context =
    item.type === 'fact' ? (item.raw as MemoryFact).contextDetails : null;

  // Check pendingCorrection for both facts and relationships
  const isPendingCorrection =
    item.type === 'fact'
      ? (item.raw as MemoryFact).pendingCorrection
      : (item.raw as MemoryRelationship).pendingCorrection;

  const handleDeclineClick = () => {
    setMode('correcting');
  };

  const handleCorrectionSubmit = () => {
    if (correctionText.trim()) {
      onDecline(item, correctionText);
      setCorrectionText('');
      setMode('view');
    }
  };

  const handleVoiceTranscription = (transcription: string) => {
    setCorrectionText((prev) => prev + (prev ? ' ' : '') + transcription);
  };

  const handleCancelCorrection = () => {
    setCorrectionText('');
    setMode('view');
  };

  const cardClasses = [
    'memory-card',
    mode === 'correcting' ? 'correcting' : '',
    isPendingCorrection ? 'pending-correction' : '',
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <div className={cardClasses}>
      <div className='memory-card-main'>
        <div className='memory-card-header'>
          <div className='memory-card-tags'>
            {item.tags.map((tag, index) => (
              <span key={index} className='memory-card-tag'>
                {tag}
              </span>
            ))}
          </div>
          {isPendingCorrection && (
            <span className='memory-card-rewriting-badge'>Rewriting...</span>
          )}
          {mode === 'correcting' && !isPendingCorrection && (
            <div className='memory-card-correction-actions'>
              <button
                className='memory-card-btn secondary'
                onClick={handleCancelCorrection}
              >
                Cancel
              </button>
              <button
                className='memory-card-btn primary'
                onClick={handleCorrectionSubmit}
                disabled={!correctionText.trim()}
              >
                Save
              </button>
            </div>
          )}
        </div>

        <div className='memory-card-content'>
          <p className='memory-card-text'>{item.displayText}</p>
          {context && <p className='memory-card-context'>{context}</p>}
        </div>
      </div>

      {!isPendingCorrection && (
        <div className='memory-card-actions'>
          {mode === 'view' ? (
            <>
              <div className='memory-card-actions-primary'>
                {!item.approved && (
                  <button
                    className='memory-card-btn primary'
                    onClick={() => onApprove(item)}
                  >
                    <ApproveIcon />
                    Yes, that's right
                  </button>
                )}
              </div>
              <button
                className='memory-card-btn tertiary'
                onClick={handleDeclineClick}
              >
                <DeclineIcon />
                No, I want to change that
              </button>
            </>
          ) : (
            <div className='memory-card-correction'>
              <div className='memory-card-correction-input-wrapper'>
                <input
                  type='text'
                  className='memory-card-correction-input'
                  placeholder='How do you want to change this?'
                  value={correctionText}
                  onChange={(e) => setCorrectionText(e.target.value)}
                  onKeyDown={(e) =>
                    e.key === 'Enter' && handleCorrectionSubmit()
                  }
                />
                <VoiceRecorder onTranscriptionDone={handleVoiceTranscription} />
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default MemoryCard;
